'use client';

/**
 * ObeyaKpiCard — KPI tile strip (RFQs · Cost Plans).
 *
 * Red/Amber used to be their own standalone tiles; folded into chips
 * on the RFQs tile instead — for this data, `summary.red` isn't a
 * separate cross-entity signal, it's literally the same RFQs the
 * Board already flags "Overdue" (confirmed live: red rag_status only
 * has a matching rule for RFQs today, POs have no red path, and
 * vendors never evaluate at all — a separate, unfixed gap). Showing
 * the same two numbers as a plain, standalone tile duplicated what
 * the RFQs count already implies rather than adding information.
 *
 * @see klyde_ipe/components/widgets/obeya-widget.tsx (legacy)
 */

import { useMemo } from 'react';
import { LayoutGrid, DollarSign, AlertCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { text, surfaceBase } from '@/lib/design/tokens.x';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useTabStore } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { usePresetStore } from '@/lib/stores/preset-store';
import { useObeyaProjectData } from './use-obeya-project-data';

export function ObeyaKpiCard() {
  const { data, isLoading, projectId, queryKey } = useObeyaProjectData();
  const { contentKey } = useCardIdentity();
  const openTab = useTabStore((s) => s.openTab);
  const setMcpContext = useGraphSelectionStore((s) => s.setMcpContext);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const { mode, strategySelection } = useViewportSelection();
  const setActivePreset = usePresetStore((s) => s.setActive);

  // Scene export — register the KPI strip into the capture envelope.
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_kpi',
    status: (data ? 'loaded' : projectId ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: projectId ? queryKey : null,
    text: data
      ? `${data.rfqs.length} RFQs · ${data.mcp_count} cost plans · ${data.summary.red} red · ${data.summary.amber} amber`
      : 'KPIs',
    json: data
      ? { project_id: projectId ?? null, rfqs: data.rfqs.length, mcp_count: data.mcp_count, latest_mcp_sid: data.latest_mcp_sid ?? null, red: data.summary.red, amber: data.summary.amber }
      : { project_id: projectId ?? null },
  }), [contentKey, data, projectId, queryKey]));

  if (!projectId) {
    return (
      <CardShell>
        <Empty msg="Select a project" />
      </CardShell>
    );
  }
  if (isLoading || !data) {
    return (
      <CardShell>
        <Empty msg="Loading…" />
      </CardShell>
    );
  }

  // Navigate to the project's most recent CostPlan — same three-call
  // sequence McpRow (project-list-card.tsx) already uses for this
  // exact entity type: openTab first (it clears selection internally),
  // then setMcpContext (anchors costplan-graph-card.tsx) and selectNode
  // (type:'mcp', scoped to this project) after.
  const mcpSid = data.latest_mcp_sid;
  const mcpName = data.latest_mcp_name;
  const openLatestCostPlan = () => {
    if (!mcpSid) return;
    openTab({ id: `mcp-${mcpSid}`, type: 'mcp', title: mcpName || 'Cost Plan', entityId: mcpSid });
    setMcpContext(mcpSid, mcpName || 'Cost Plan');
    selectNode({ id: mcpSid, label: mcpName || 'Cost Plan', type: 'mcp', properties: {} }, projectId);
  };

  // Same-entity preset switch (stays on this project, just changes
  // which tab paints) — the exact call PresetSwitcher's own chips make
  // (`setActive(mode, selection, key)`), not the openTab/selectNode
  // cross-entity pattern the Cost Plans tile uses above. No entity
  // state to touch: strategySelection is read as-is to key the store.
  const openBoard = () => setActivePreset(mode, strategySelection, 'mode1_board');

  return (
    <CardShell>
      <div className="grid grid-cols-2 gap-3">
        {/* RFQs — jumps to the Board tab (same project, different
            preset). Red/Amber now live here as chips, not separate
            tiles — same two numbers as before, just relocated. */}
        <button
          type="button"
          onClick={openBoard}
          title="Open Board"
          className="bg-card rounded-xl border border-border p-3 text-left hover:border-primary/40 hover:bg-muted/50 transition-colors cursor-pointer"
        >
          <div className="flex items-center justify-between mb-1">
            <span className={text.eyebrow}>RFQs</span>
            <LayoutGrid className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <p className={text.value}>{data.rfqs.length}</p>
            <span className={cn(
              'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium',
              data.summary.red > 0 ? 'bg-red-400/10 text-red-400' : 'bg-muted text-muted-foreground',
            )}>
              <AlertCircle className="w-3 h-3" />{data.summary.red}
            </span>
            <span className={cn(
              'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium',
              data.summary.amber > 0 ? 'bg-amber-400/10 text-amber-400' : 'bg-muted text-muted-foreground',
            )}>
              <AlertTriangle className="w-3 h-3" />{data.summary.amber}
            </span>
          </div>
        </button>
        {/* Cost Plans — same styling, but a real button: jumps to the
            project's most recent CostPlan. Disabled (no navigation,
            dimmed) when the project has none yet. */}
        <button
          type="button"
          disabled={!mcpSid}
          onClick={openLatestCostPlan}
          title={mcpSid ? `Open ${mcpName || 'Cost Plan'}` : 'No cost plan yet'}
          className={cn(
            'bg-card rounded-xl border border-border p-3 text-left',
            mcpSid ? 'hover:border-primary/40 hover:bg-muted/50 transition-colors cursor-pointer' : 'cursor-default opacity-60',
          )}
        >
          <div className="flex items-center justify-between mb-1">
            <span className={text.eyebrow}>Cost Plans</span>
            <DollarSign className={cn('w-3.5 h-3.5', mcpSid ? 'text-emerald-400' : 'text-muted-foreground')} />
          </div>
          <p className={text.value}>{data.mcp_count}</p>
        </button>
      </div>
    </CardShell>
  );
}

function CardShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-md border border-border bg-card p-3 shadow-sm" style={{ fontSize: surfaceBase.canvas }}>{children}</div>;
}

function Empty({ msg }: { msg: string }) {
  return <div className={cn(text.muted, 'py-2')}>{msg}</div>;
}
